import type { Cents } from "../domain/money";
import type { QuantityUnit } from "../domain/feature";
import type { RateSurface } from "../domain/rateUnit";
import { rateUnitWords } from "../domain/rateUnit";
import { managementKeyOf, type CategoryDetailLine } from "./categoryBudget";

/**
 * CLOSING OUT A RENOVATION (spec §91-2/3/4).
 *
 * §90 could learn what a company actually pays, but only from work marked
 * complete — and marking work complete meant finding every scope row by
 * hand, which is exactly the kind of bookkeeping a normal user forgets.
 * This engine is the read model behind ONE focused pass over a finished
 * project. It is pure: it decides nothing about money, writes nothing, and
 * every state it reports is derived from records that already exist.
 *
 * THE THREE HONESTY RULES this file exists to keep:
 *
 *   1. EVIDENCE IS NOT A DECISION. A recorded expense may be a deposit, a
 *      partial payment or a pile of material sitting in the garage; a
 *      finished schedule task is somebody's judgement about time, not about
 *      money. Either makes an item READY TO REVIEW and says why. Neither
 *      ever reads as complete on its own.
 *   2. COMPLETION IS NOT COST. Nothing here creates, moves or infers a
 *      dollar: the completion decision only tells estimating which results
 *      are final.
 *   3. A FINISHED PROJECT MAY LEGITIMATELY TEACH NOTHING (spec §91-23).
 *      Owner labour, a cancelled item, an allowance with no quantity — each
 *      is reported with its reason instead of being forced into history.
 *
 * Identity is the §78 management identity (kind of work + action), so the
 * Completion Review, the Renovation Budget and the bid sheet name the same
 * things the same way. There is no second grouping system.
 */

/** What the project's own records say about a piece of work. */
export type WorkCompletionState =
  /** Somebody marked it complete — the authoritative answer. */
  | "complete"
  /** Nobody has said, but the project holds evidence worth reviewing. */
  | "ready-to-review"
  /** Marked in progress. */
  | "in-progress"
  /** Nothing has been said and nothing has happened. */
  | "not-started";

/** Why a completed item still teaches estimating nothing. */
export type LearningBlocker =
  | "not-complete"
  | "no-actual-cost"
  | "no-quantity"
  | "no-unit"
  | "not-priced-by-a-rate"
  | "excluded";

export interface CompletionFeatureInput {
  featureId: string;
  featureDefinitionId: string | null;
  name: string;
  action: string | null;
  roomId: string | null;
  locationLabel: string;
  /** The §74 trade this work is managed under. */
  categoryId: string;
  categoryDisplayName: string;
  categorySortOrder: number;
  /** project_features.work_status — the ONE completion truth. */
  workStatus: string | null;
  proposedCents: Cents;
  /**
   * Reconciled actual money attributable to this item, as every other
   * screen shows it: tax included, net of returns.
   */
  actualCents: Cents;
  /**
   * The same money EXCLUDING sales tax — the §9E historical basis, and the
   * only figure learning is allowed to use, because the estimate adds its
   * own estimating tax and a tax-inclusive rate would count it twice.
   */
  actualBeforeTaxCents: Cents;
  /** True when the only money against it has been voided. */
  hasVoidedMoney: boolean;
  /** Its natural quantity, when anything measured one. */
  quantityX100: number | null;
  quantityUnit: QuantityUnit | null;
  surface: RateSurface | null;
  /** Linked schedule tasks, and how many are finished (evidence only). */
  scheduleTaskCount: number;
  scheduleFinishedCount: number;
  /** A selected contractor quote covers this work (evidence, never money). */
  quotedByContractor: string | null;
  /** Somebody asked estimating not to learn from this observation. */
  exclusion: { reasonCode: string; reason: string } | null;
}

export interface CompletionLearning {
  /** Will this observation reach estimating history once it is complete? */
  willTeach: boolean;
  /** Its actual cost per natural unit, x100 cents. Null when there is none. */
  unitCostCentsX100: number | null;
  /** "per floor sq ft" / "each" — how that figure reads. */
  unitWords: string;
  /** Plain-English reason, always present. */
  reason: string;
  blocker: LearningBlocker | null;
}

export interface CompletionMember {
  featureId: string;
  name: string;
  locationLabel: string;
  state: WorkCompletionState;
  evidence: string[];
  proposedCents: Cents;
  actualCents: Cents;
  quantityText: string | null;
  quotedByContractor: string | null;
  learning: CompletionLearning;
  exclusion: { reasonCode: string; reason: string } | null;
}

export interface CompletionItem {
  /** The §78 management identity. */
  key: string;
  label: string;
  featureIds: string[];
  /** The weakest member's answer: an item is complete when all of it is. */
  state: WorkCompletionState;
  evidence: string[];
  proposedCents: Cents;
  actualCents: Cents;
  completeCount: number;
  featureCount: number;
  willTeachCount: number;
  members: CompletionMember[];
}

export interface CompletionTradeGroup {
  categoryId: string;
  displayName: string;
  sortOrder: number;
  items: CompletionItem[];
  proposedCents: Cents;
  actualCents: Cents;
  featureCount: number;
  completeCount: number;
  /** Every item of this trade is finished. */
  allComplete: boolean;
}

export interface CompletionSummary {
  itemCount: number;
  featureCount: number;
  completeCount: number;
  readyToReviewCount: number;
  unfinishedCount: number;
  withActualCount: number;
  withoutActualCount: number;
  /** Observations that WILL reach estimating history as things stand. */
  willTeachCount: number;
  wontTeachCount: number;
  excludedCount: number;
  proposedCents: Cents;
  actualCents: Cents;
}

export interface CompletionReview {
  trades: CompletionTradeGroup[];
  summary: CompletionSummary;
  /**
   * The state-driven nudge (spec §91-22), or null when there is nothing
   * worth saying. Never urgency, never a repeated nag: one sentence of
   * fact, built from the project's own counts.
   */
  prompt: string | null;
  /** What still wants a decision before the renovation is called finished. */
  blockers: string[];
}

const QUANTITY_WORDS: Readonly<Record<string, string>> = {
  each: "",
  sqft: "sq ft",
  lf: "lf",
  square: "squares",
  ls: "",
};

function quantityText(feature: CompletionFeatureInput): string | null {
  if (feature.quantityX100 === null || feature.quantityX100 <= 0 || feature.quantityUnit === null) return null;
  const value = feature.quantityX100 / 100;
  const shown = Number.isInteger(value) ? String(value) : value.toFixed(2);
  const words = QUANTITY_WORDS[feature.quantityUnit] ?? feature.quantityUnit;
  return words === "" ? shown : `${shown} ${words}`;
}

/**
 * Does this observation reach estimating history? (spec §91-8)
 *
 * Every §90 safeguard is kept and none is relaxed to make the sample look
 * bigger: truth matters more than count. The order is the order a person
 * would ask the questions in, so the reason they are shown is the FIRST
 * thing that is actually missing.
 */
export function learningEligibility(feature: CompletionFeatureInput): CompletionLearning {
  const unitWords =
    feature.quantityUnit === null
      ? ""
      : rateUnitWords(
          feature.quantityUnit === "sqft" ? "sqft" : feature.quantityUnit === "lf" ? "lf" : feature.quantityUnit === "square" ? "square" : "each",
          feature.surface,
        );
  const deny = (blocker: LearningBlocker, reason: string): CompletionLearning => ({
    willTeach: false,
    unitCostCentsX100: null,
    unitWords,
    reason,
    blocker,
  });

  if (feature.exclusion) {
    return deny("excluded", `Excluded from estimating history${feature.exclusion.reason ? ` — ${feature.exclusion.reason}` : ""}.`);
  }
  if (feature.featureDefinitionId === null) {
    return deny("not-priced-by-a-rate", "This is one-off work, so there is no kind of work for a rate to belong to.");
  }
  if (feature.workStatus !== "complete") {
    return deny("not-complete", "Not marked complete — unfinished work teaches nothing.");
  }
  if (feature.actualBeforeTaxCents <= 0) {
    /*
     * FOUND BY USING IT. This said "Work you did yourself is not a market
     * labour rate" for every item with no money against it, which reads as
     * an accusation about work the software knows nothing about — most of
     * these are simply items nobody has invoiced yet. The owner-labour
     * point is still made (it is the reason $0 is never learned, spec
     * §91-30) but as an explanation, not as a claim about what happened.
     */
    return deny(
      "no-actual-cost",
      feature.hasVoidedMoney
        ? "The money recorded against it was voided, so there is no actual cost to learn from."
        : "No actual cost is recorded against it, so there is no price to learn. Work nobody was invoiced for — your own labour, or a favour — records nothing, and free is not a rate.",
    );
  }
  if (feature.quantityX100 === null || feature.quantityX100 <= 0) {
    return deny("no-quantity", "No quantity was measured, so a cost per unit cannot be worked out.");
  }
  if (feature.quantityUnit === null) {
    return deny("no-unit", "Its quantity does not say what it is measured in, and nothing is converted behind your back.");
  }
  return {
    willTeach: true,
    unitCostCentsX100: Math.round((feature.actualBeforeTaxCents * 100 * 100) / feature.quantityX100),
    unitWords,
    reason: "Counts towards what this kind of work actually costs per unit.",
    blocker: null,
  };
}

/**
 * What the project's records suggest, and why (spec §91-4).
 *
 * The evidence sentences are the whole point: a user who is told "money has
 * been recorded against this" can decide in a second, while a user shown a
 * tick they did not put there has to go and check whether the software is
 * lying to them.
 */
export function completionEvidence(feature: CompletionFeatureInput): { state: WorkCompletionState; evidence: string[] } {
  const evidence: string[] = [];
  if (feature.actualCents > 0) evidence.push("Actual cost has been reconciled to it.");
  else if (feature.hasVoidedMoney) evidence.push("The money recorded against it was voided.");
  if (feature.scheduleTaskCount > 0 && feature.scheduleFinishedCount === feature.scheduleTaskCount) {
    evidence.push(feature.scheduleTaskCount === 1 ? "Its schedule task is finished." : `All ${feature.scheduleTaskCount} of its schedule tasks are finished.`);
  } else if (feature.scheduleFinishedCount > 0) {
    evidence.push(`${feature.scheduleFinishedCount} of ${feature.scheduleTaskCount} schedule tasks are finished.`);
  }
  if (feature.quotedByContractor) evidence.push(`${feature.quotedByContractor}'s quote was selected for it.`);

  if (feature.workStatus === "complete") return { state: "complete", evidence };
  if (feature.workStatus === "in-progress") return { state: "in-progress", evidence };
  // EVIDENCE NEVER COMPLETES ANYTHING. Money or a finished task raises the
  // item for review; the decision stays the user's (spec §91-4).
  const hasEvidence =
    feature.actualCents > 0 || (feature.scheduleTaskCount > 0 && feature.scheduleFinishedCount === feature.scheduleTaskCount);
  return { state: hasEvidence ? "ready-to-review" : "not-started", evidence };
}

const STATE_RANK: Record<WorkCompletionState, number> = {
  complete: 3,
  "ready-to-review": 2,
  "in-progress": 1,
  "not-started": 0,
};

export function reviewCompletion(features: readonly CompletionFeatureInput[]): CompletionReview {
  const byCategory = new Map<string, CompletionFeatureInput[]>();
  for (const feature of features) {
    const list = byCategory.get(feature.categoryId);
    if (list) list.push(feature);
    else byCategory.set(feature.categoryId, [feature]);
  }

  const trades: CompletionTradeGroup[] = [];
  const summary: CompletionSummary = {
    itemCount: 0,
    featureCount: 0,
    completeCount: 0,
    readyToReviewCount: 0,
    unfinishedCount: 0,
    withActualCount: 0,
    withoutActualCount: 0,
    willTeachCount: 0,
    wontTeachCount: 0,
    excludedCount: 0,
    proposedCents: 0,
    actualCents: 0,
  };

  for (const [categoryId, list] of byCategory) {
    const groups = new Map<string, CompletionFeatureInput[]>();
    for (const feature of list) {
      // The §78 identity, asked of the same shape the budget asks it of.
      const detail: Pick<CategoryDetailLine, "featureId" | "featureDefinitionId" | "action" | "name"> = {
        featureId: feature.featureId,
        featureDefinitionId: feature.featureDefinitionId,
        action: feature.action,
        name: feature.name,
      };
      const key = managementKeyOf(detail);
      const group = groups.get(key);
      if (group) group.push(feature);
      else groups.set(key, [feature]);
    }

    const items: CompletionItem[] = [];
    for (const [key, members] of groups) {
      const rendered: CompletionMember[] = members.map((feature) => {
        const { state, evidence } = completionEvidence(feature);
        return {
          featureId: feature.featureId,
          name: feature.name,
          locationLabel: feature.locationLabel,
          state,
          evidence,
          proposedCents: feature.proposedCents,
          actualCents: feature.actualCents,
          quantityText: quantityText(feature),
          quotedByContractor: feature.quotedByContractor,
          learning: learningEligibility(feature),
          exclusion: feature.exclusion,
        };
      });
      const completeCount = rendered.filter((m) => m.state === "complete").length;
      // An item is only as finished as its least finished member.
      const weakest = rendered.reduce(
        (worst, m) => (STATE_RANK[m.state] < STATE_RANK[worst] ? m.state : worst),
        "complete" as WorkCompletionState,
      );
      const base = members[0].name.trim();
      const where =
        rendered.length === 1 && rendered[0].locationLabel ? ` — ${rendered[0].locationLabel}` : rendered.length > 1 ? ` (${rendered.length})` : "";
      items.push({
        key,
        label: base + where,
        featureIds: members.map((m) => m.featureId),
        state: weakest,
        evidence: [...new Set(rendered.flatMap((m) => m.evidence))],
        proposedCents: rendered.reduce((s, m) => s + m.proposedCents, 0),
        actualCents: rendered.reduce((s, m) => s + m.actualCents, 0),
        completeCount,
        featureCount: rendered.length,
        willTeachCount: rendered.filter((m) => m.learning.willTeach).length,
        members: rendered,
      });

      for (const member of rendered) {
        summary.featureCount += 1;
        summary.proposedCents += member.proposedCents;
        summary.actualCents += member.actualCents;
        if (member.state === "complete") summary.completeCount += 1;
        else if (member.state === "ready-to-review") summary.readyToReviewCount += 1;
        else summary.unfinishedCount += 1;
        if (member.actualCents > 0) summary.withActualCount += 1;
        else summary.withoutActualCount += 1;
        if (member.learning.willTeach) summary.willTeachCount += 1;
        else summary.wontTeachCount += 1;
        if (member.exclusion) summary.excludedCount += 1;
      }
      summary.itemCount += 1;
    }

    items.sort((a, b) => b.proposedCents - a.proposedCents || a.label.localeCompare(b.label));
    const featureCount = items.reduce((s, i) => s + i.featureCount, 0);
    const completeCount = items.reduce((s, i) => s + i.completeCount, 0);
    trades.push({
      categoryId,
      displayName: list[0].categoryDisplayName,
      sortOrder: list[0].categorySortOrder,
      items,
      proposedCents: items.reduce((s, i) => s + i.proposedCents, 0),
      actualCents: items.reduce((s, i) => s + i.actualCents, 0),
      featureCount,
      completeCount,
      allComplete: featureCount > 0 && completeCount === featureCount,
    });
  }

  trades.sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName));

  const blockers: string[] = [];
  if (summary.readyToReviewCount > 0) {
    blockers.push(
      `${summary.readyToReviewCount} work item${summary.readyToReviewCount === 1 ? " has" : "s have"} evidence of being finished but ${
        summary.readyToReviewCount === 1 ? "is" : "are"
      } not marked complete.`,
    );
  }
  if (summary.unfinishedCount > 0) {
    blockers.push(`${summary.unfinishedCount} work item${summary.unfinishedCount === 1 ? "" : "s"} still appear unfinished.`);
  }
  const completeWithoutMoney = trades
    .flatMap((t) => t.items)
    .flatMap((i) => i.members)
    .filter((m) => m.state === "complete" && m.actualCents <= 0).length;
  if (completeWithoutMoney > 0) {
    blockers.push(`${completeWithoutMoney} completed work item${completeWithoutMoney === 1 ? " has" : "s have"} no recorded actual cost.`);
  }
  if (summary.wontTeachCount > 0) {
    blockers.push(
      `${summary.wontTeachCount} work item${summary.wontTeachCount === 1 ? "" : "s"} will not be used for estimating history.`,
    );
  }

  const prompt =
    summary.readyToReviewCount > 0
      ? `${summary.readyToReviewCount} work item${summary.readyToReviewCount === 1 ? " has" : "s have"} actual costs or finished tasks but ${
          summary.readyToReviewCount === 1 ? "is" : "are"
        } not marked complete. Review completion to include finished work in future estimating history.`
      : null;

  return { trades, summary, prompt, blockers };
}
